import { useAuth } from "../context/AuthContext";
import * as allauth from "../library/allauth";
import { useState, useEffect } from "react";
import { User, Shield, MapPin, Sliders, Bell } from "lucide-react";
import Navbar from "../components/navigation/Navbar";
import Xroadz_Logo from "../img/XRoadz_Logo.png";

export default function Profile() {
  const { user } = useAuth();
  const [userGroups, setUserGroups] = useState([]);
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [regions, setRegions] = useState([]);
  const [features, setFeatures] = useState({});
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [dateJoined, setDateJoined] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    allauth.getUserGroups().then((res) => {
      if (res.status === 200) {
        setUserGroups(res.data.groups || []);
        setUserFirstName(res.data.first_name || "");
        setUserLastName(res.data.last_name || "");
        setFirstName(res.data.first_name || "");
        setLastName(res.data.last_name || "");
        setFeatures(res.data.features || {});
        setDateJoined(res.data.date_joined || null);
      }
    });
    allauth.getUserRegions &&
      allauth.getUserRegions().then((res) => {
        if (res?.status === 200) setRegions(res.data || []);
      });

    // Add this inside the existing useEffect after getUserRegions call:
    allauth.getTOTPAuthenticator().then((res) => {
      setMfaEnabled(res.status === 200);
    });
  }, []);

  const getUserRole = () => {
    if (userGroups.includes("xroadz_admin")) return "Super Admin";
    if (userGroups.includes("customer_admin")) return "Customer Admin";
    if (userGroups.includes("customer_user")) return "Customer User";
    return "User";
  };

  const getRoleBadgeColor = () => {
    if (userGroups.includes("xroadz_admin"))
      return "bg-purple-100 text-purple-700";
    if (userGroups.includes("customer_admin"))
      return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-600";
  };

  const getAvatarColor = () => {
    if (userGroups.includes("xroadz_admin")) return "bg-purple-500";
    if (userGroups.includes("customer_admin")) return "bg-blue-500";
    return "bg-emerald-700";
  };
  const [editMode, setEditMode] = useState(false);
  const displayName =
    userFirstName || userLastName
      ? `${userFirstName} ${userLastName}`.trim()
      : user?.username || "User";

  const [avatarPreview, setAvatarPreview] = useState(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };
  const handleSave = async () => {
    setSaving(true);
    setSuccessMsg("");
    try {
      const res = await allauth.updateProfile({
        first_name: firstName,
        last_name: lastName,
      });
      if (res?.status === 200) {
        setUserFirstName(firstName);
        setUserLastName(lastName);
        setSuccessMsg("Profile updated successfully.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header Card */}
          <div className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
            {/* Cover Banner */}
            <div className="h-32 bg-emerald-700 relative overflow-hidden">
              <div className="absolute w-72 h-72 rounded-full bg-emerald-600 opacity-10 -top-24 -right-24" />
              <div className="absolute w-48 h-48 rounded-full bg-emerald-900 opacity-20 -bottom-16 right-56" />
            </div>

            {/* Bottom section */}
            <div className="px-6 pb-3">
              {/* Avatar overlapping banner */}
              <div className="flex items-end justify-between -mt-14 mb-2">
                <div className="relative">
                  <div
                    className={`w-32 h-32 rounded-full ${getAvatarColor()} flex items-center justify-center text-white text-4xl font-bold border-4 border-white shadow overflow-hidden`}
                  >
                    {" "}
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      displayName[0]?.toUpperCase() || "U"
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-5 h-5 bg-emerald-700 rounded-full flex items-center justify-center cursor-pointer border-2 border-white hover:bg-emerald-800 transition-colors">
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </label>
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}
                  >
                    {getUserRole()}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Active
                  </span>
                </div>
              </div>

              <h1 className="text-lg font-semibold text-gray-900">
                {displayName}
              </h1>
              <p className="text-sm text-gray-400">
                {user?.email || user?.username}
              </p>
              <p className="text-xs text-gray-400">
                Joined{" "}
                {dateJoined ? new Date(dateJoined).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>

          {/* 2 Column Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Account Info Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <User size={15} className="text-emerald-600" />
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Account Info
                  </p>
                </div>

                {successMsg && (
                  <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    {successMsg}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">First name</p>
                      {editMode ? (
                        <input
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="First name"
                        />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">
                          {firstName || "—"}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Last name</p>
                      {editMode ? (
                        <input
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Last name"
                        />
                      ) : (
                        <p className="text-sm font-medium text-gray-800">
                          {lastName || "—"}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-1">
                      Username / Email
                    </p>
                    <p className="text-sm font-medium text-gray-800">
                      {user?.username || "—"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Username cannot be changed
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-1">Role</p>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}
                    >
                      {getUserRole()}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      Assigned by your administrator
                    </p>
                  </div>

                  {!editMode ? (
                    <button
                      onClick={() => setEditMode(true)}
                      className="w-full py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setFirstName(userFirstName);
                          setLastName(userLastName);
                          setEditMode(false);
                          setSuccessMsg("");
                        }}
                        className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          await handleSave();
                          setEditMode(false);
                        }}
                        disabled={saving}
                        className="flex-1 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Access Summary Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <MapPin size={15} className="text-blue-500" />
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Access Summary
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Regions</p>
                    {regions.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {regions.map((r) => (
                          <span
                            key={r.id}
                            className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                          >
                            {r.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">
                        No regions assigned
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-2">
                      Features enabled
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(features)
                        .filter(([, val]) => val)
                        .map(([key]) => (
                          <span
                            key={key}
                            className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 capitalize"
                          >
                            {key}
                          </span>
                        ))}
                    </div>
                  </div>

                  <p className="text-xs text-gray-400">
                    Managed by your administrator
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Security Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Shield size={15} className="text-purple-500" />
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Security
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Password
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Change your account password
                      </p>
                    </div>
                    <a
                      href="/settings"
                      className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Change
                    </a>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Two-factor authentication
                      </p>
                      {mfaEnabled ? (
                        <p className="text-xs text-emerald-600 mt-0.5">
                          ✓ Enabled
                        </p>
                      ) : (
                        <p className="text-xs text-red-500 mt-0.5">
                          Not enabled
                        </p>
                      )}
                    </div>
                    <a
                      href="/settings"
                      className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        mfaEnabled
                          ? "text-gray-600 border border-gray-200 hover:bg-gray-50"
                          : "text-white bg-emerald-700 hover:bg-emerald-800"
                      }`}
                    >
                      {mfaEnabled ? "Manage MFA" : "Enable MFA"}
                    </a>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Active sessions
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Manage where you're logged in
                      </p>
                    </div>
                    <a
                      href="/settings"
                      className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      View
                    </a>
                  </div>
                </div>
              </div>

              {/* Notifications Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Bell size={15} className="text-amber-500" />
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Notifications
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    {
                      label: "Permission change alerts",
                      sub: "Email when your access changes",
                      key: "permission",
                    },
                    {
                      label: "Login alerts",
                      sub: "Email on new sign-in",
                      key: "login",
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {item.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {item.sub}
                        </p>
                      </div>
                      <div className="w-10 h-5 rounded-full bg-gray-200 flex items-center px-0.5 cursor-not-allowed opacity-50">
                        <div className="w-4 h-4 bg-white rounded-full shadow" />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">
                    Notification settings coming soon
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
